import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import StarRating from '../ui/StarRating';
import { formatCurrency, discountPercent } from '../../utils/format';

// Whether an element's content is actually being clipped by line-clamp right
// now — re-checked on resize since the clamp's line count (and the card's
// width, which changes how much text fits per line) both shift across
// breakpoints. A ResizeObserver on the element itself covers both without
// needing a window-level listener.
function useIsClamped() {
  const ref = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setIsClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  });

  return [ref, isClamped];
}

// Detailed single-book presentation — cover plus title, rating, description,
// and price/CTA — used wherever a section has too few books to justify a
// scrollable carousel (see BookSection) and by the hand-curated Featured
// Books banner. `size="lg"` is for when it's the only card in the section
// (full-width, more description room) rather than sharing space with a
// sibling card.
export default function BookSpotlightCard({ book, size = 'md', eyebrow }) {
  const discount = discountPercent(book.price, book.mrp);
  const isLarge = size === 'lg';
  const [descRef, isDescClamped] = useIsClamped();

  return (
    <div className={`flex flex-col items-center gap-4 p-6 text-center sm:flex-row sm:items-center sm:text-left ${isLarge ? 'sm:gap-10 sm:p-10' : 'sm:gap-6 sm:p-8'}`}>
      <Link to={`/book/${book.slug}`} className={`block shrink-0 ${isLarge ? 'w-36 sm:w-48' : 'w-28 sm:w-36'}`}>
        <div className="aspect-[2/3] overflow-hidden rounded-xl2 shadow-spine">
          <img src={book.cover} alt={`Cover of ${book.title}`} className="h-full w-full object-cover" />
        </div>
      </Link>

      <div className="min-w-0">
        {eyebrow && (
          <span className="eyebrow inline-flex items-center gap-2">
            <Sparkles size={14} /> {eyebrow}
          </span>
        )}
        <Link to={`/book/${book.slug}`}>
          <h3 className={`mt-2 font-display font-semibold text-slate-900 dark:text-white line-clamp-2 hover:text-primary-600 dark:hover:text-primary-300 transition-colors ${isLarge ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}>
            {book.title}
          </h3>
        </Link>
        {book.author && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">by {book.author.name}</p>
        )}
        <div className="mt-2 flex justify-center sm:justify-start">
          <StarRating rating={book.rating} reviewsCount={book.reviewsCount} />
        </div>
        {book.description && (
          // The link sits outside the clamped <p> (line-clamp needs the
          // -webkit-box display all to itself — pairing it with a `block`
          // utility on the same element cancels the clamp) and is instead
          // pinned to the paragraph's bottom-right corner, with a matching
          // background to mask the clipped text/ellipsis behind it so it
          // reads as sitting at the end of the last line.
          <div className="relative mt-2 hidden sm:block">
            <p ref={descRef} className={`text-sm text-slate-600 dark:text-slate-300 ${isLarge ? 'line-clamp-6' : 'line-clamp-4'}`}>
              {book.description}
            </p>
            {isDescClamped && (
              <Link
                to={`/book/${book.slug}`}
                className="absolute bottom-0 right-0 bg-white/90 pl-2 text-xs font-semibold text-primary-600 backdrop-blur-sm hover:underline dark:bg-surface-darkCard/90 dark:text-primary-300"
              >
                more...
              </Link>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-base font-semibold text-slate-900 dark:text-white">
              {formatCurrency(book.price)}
            </span>
            {discount > 0 && (
              <>
                <span className="font-mono text-xs text-slate-400 line-through">{formatCurrency(book.mrp)}</span>
                <span className="text-xs font-medium text-success">{discount}% off</span>
              </>
            )}
          </div>
          <Link to={`/book/${book.slug}`} className="btn-primary !px-4 !py-2 text-sm">
            View Book <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
